import { Avatar, AvatarFallback } from '@/components/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/sidebar'
import {
  selectAllRegions,
  selectSelectedRegion,
  setSelectedRegionId
} from '@frontend/core/lib/domains/regions'
import { useAppDispatch, useAppSelector } from '@frontend/react'
import { Button } from '@frontend/ui'
import { updateUrlQuery } from '@frontend/utils'
import { ChevronsUpDown } from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export function RegionSwitcher() {
  const dispatch = useAppDispatch()
  const regionsData = useAppSelector(selectAllRegions)
  const selectedRegion = useAppSelector(selectSelectedRegion)

  const regionOptions = useMemo(() => {
    return regionsData.map((region) => ({
      name: region.name,
      flag: region.flag,
      value: region.id
    }))
  }, [regionsData])

  useEffect(() => {
    if (!selectedRegion && regionsData.length > 0) {
      dispatch(setSelectedRegionId(regionsData[0].id))
    }
  }, [selectedRegion, regionsData])

  if (!selectedRegion) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          className='w-auto justify-between gap-2 bg-transparent'
        >
          <div className='flex items-center gap-2'>
            <Avatar className='h-6 w-6 rounded-lg'>
              <AvatarFallback className='rounded-lg bg-transparent'>
                {selectedRegion.flag}
              </AvatarFallback>
            </Avatar>
            <span className='truncate font-medium'>{selectedRegion.name}</span>
          </div>
          <ChevronsUpDown className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='min-w-[200px] rounded-lg' align='start'>
        <DropdownMenuLabel className='text-muted-foreground text-xs'>
          Regions
        </DropdownMenuLabel>
        {regionOptions.map((option, index) => (
          <DropdownMenuItem
            key={option.name}
            onClick={() => dispatch(setSelectedRegionId(option.value))}
            className='gap-2 p-2'
          >
            <Avatar className='h-6 w-6 rounded-lg'>
              <AvatarFallback className='rounded-lg bg-transparent'>
                {option.flag}
              </AvatarFallback>
            </Avatar>
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function SidebarRegionSwitcher() {
  const dispatch = useAppDispatch()
  const regionsData = useAppSelector(selectAllRegions)
  const selectedRegion = useAppSelector(selectSelectedRegion)

  const regionOptions = useMemo(() => {
    return regionsData.map((region) => ({
      name: region.name,
      flag: region.flag,
      value: region.id
    }))
  }, [regionsData])

  useEffect(() => {
    if (!selectedRegion && regionsData.length > 0) {
      dispatch(setSelectedRegionId(regionsData[0].id))
    }
  }, [selectedRegion, regionsData, dispatch])

  if (!selectedRegion) {
    return null
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <div className='flex aspect-square size-8 items-center justify-center rounded-lg text-lg'>
                {selectedRegion.flag}
              </div>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>
                  {selectedRegion.name}
                </span>
                <span className='text-muted-foreground truncate text-xs'>
                  Region
                </span>
              </div>
              <ChevronsUpDown className='ml-auto h-4 w-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-(--radix-dropdown-menu-trigger-width) min-w-[200px] rounded-lg'
            align='start'
            side='right'
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-muted-foreground text-xs'>
              Regions
            </DropdownMenuLabel>
            {regionOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => dispatch(setSelectedRegionId(option.value))}
                className='gap-2 p-2'
              >
                <span className='text-lg'>{option.flag}</span>
                {option.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export function RegionQueryPersistence() {
  const location = useLocation()

  const selectedRegion = useAppSelector(selectSelectedRegion)

  useLayoutEffect(() => {
    // Get the hash and search params from the URL //
    if (!location.search && selectedRegion) {
      updateUrlQuery('region', selectedRegion.id, location.hash)
    }
  }, [location.pathname])

  return null
}
